import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaThumbsUp } from "react-icons/fa";

import { postProveedor } from "../helpers/rutaProveedores";
import { getLocalidades } from "../helpers/rutaLocalidades";

import "../css/addclienteform.css";
import "../css/modalformalert.css";

const initialForm = {
  codprov: "",
  razonsocial: "",
  domicilio: "",
  telefono: "",
  cuit: "",
  email: "",
  localidad: "",
};

const normalizarArray = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data?.[key])) return data.data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const AddProveedorForm = ({ setShow, onSaved }) => {
  const [formValues, setFormValues] = useState(initialForm);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [localidades, setLocalidades] = useState({
    data: [],
    loading: true,
    error: "",
  });

  useEffect(() => {
    const cargarLocalidades = async () => {
      try {
        const resp = await getLocalidades();
        const lista = normalizarArray(resp, "localidades");

        setLocalidades({
          data: lista,
          loading: false,
          error: "",
        });
      } catch (e) {
        setLocalidades({
          data: [],
          loading: false,
          error: e?.message || "Error al cargar localidades",
        });
      }
    };

    cargarLocalidades();
  }, []);

  const opcionesLocalidades = useMemo(() => {
    return [...(localidades.data || [])]
      .filter((l) => l?.activo ?? true)
      .sort((a, b) =>
        String(a.localidad || "").localeCompare(String(b.localidad || ""), "es", {
          sensitivity: "base",
        })
      );
  }, [localidades.data]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((s) => ({
      ...s,
      [name]: name === "razonsocial" || name === "domicilio"
        ? value.toUpperCase()
        : value,
    }));
  };

  const validar = () => {
    if (!formValues.razonsocial.trim()) return "La razón social es obligatoria.";
    if (!formValues.domicilio.trim()) return "El domicilio es obligatorio.";

    if (formValues.codprov) {
      const cod = Number(formValues.codprov);
      if (!Number.isFinite(cod) || cod <= 0) {
        return "El código de proveedor debe ser válido.";
      }
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validar();

    if (msg) {
      setError(msg);
      return;
    }

    try {
      setGuardando(true);
      setError("");

      const payload = {
        codprov: formValues.codprov ? Number(formValues.codprov) : undefined,
        razonsocial: formValues.razonsocial.trim().toUpperCase(),
        domicilio: formValues.domicilio.trim().toUpperCase(),
        telefono: formValues.telefono.trim(),
        cuit: formValues.cuit.trim(),
        email: formValues.email.trim().toLowerCase(),
        localidad: formValues.localidad || null,
      };

      const resp = await postProveedor(payload);

      if (resp?.ok === false) {
        const backendMsg =
          resp?.err?.message ||
          resp?.error?.err?.message ||
          resp?.error?.message ||
          resp?.error ||
          "No se pudo crear el proveedor.";

        throw new Error(
          typeof backendMsg === "string"
            ? backendMsg
            : "No se pudo crear el proveedor."
        );
      }

      setFormValues(initialForm);
      setShowSuccessModal(true);

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (err) {
      setError(err?.message || "No se pudo crear el proveedor.");
    } finally {
      setGuardando(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    if (typeof setShow === "function") {
      setShow(false);
    }
  };

  if (localidades.loading) {
    return <div className="p-3">Cargando localidades...</div>;
  }

  if (localidades.error) {
    return <div className="p-3 text-danger">{localidades.error}</div>;
  }

  return (
    <>
      <div className="container">
        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group mt-3 col-sm-2">
              <label>Cod Prov</label>
              <input
                type="number"
                className="form-control"
                name="codprov"
                value={formValues.codprov}
                onChange={handleChange}
                placeholder="Auto"
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Razón Social</label>
              <input
                type="text"
                className="form-control"
                name="razonsocial"
                maxLength="80"
                required
                value={formValues.razonsocial}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Domicilio</label>
              <input
                type="text"
                className="form-control"
                name="domicilio"
                maxLength="80"
                required
                value={formValues.domicilio}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-2">
              <label>Teléfono</label>
              <input
                type="text"
                className="form-control"
                name="telefono"
                maxLength="30"
                value={formValues.telefono}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group mt-3 col-sm-2">
              <label>CUIT</label>
              <input
                type="text"
                className="form-control"
                name="cuit"
                maxLength="20"
                value={formValues.cuit}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                name="email"
                maxLength="80"
                value={formValues.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Localidad</label>
              <select
                className="form-control"
                name="localidad"
                value={formValues.localidad}
                onChange={handleChange}
              >
                <option value="">Sin localidad</option>
                {opcionesLocalidades.map((localidad) => (
                  <option key={localidad._id} value={localidad._id}>
                    {localidad.localidad}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-dark mt-5 mb-3"
            id="button"
            disabled={guardando}
          >
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </form>
      </div>

      <Modal show={showSuccessModal} onHide={handleSuccessClose} centered>
        <div className="custom-modal">
          <div className="custom-modal-icon">
            <FaThumbsUp />
          </div>

          <Modal.Body className="custom-modal-body">
            <h4>Info</h4>
            <p>¡Proveedor agregado correctamente!</p>
          </Modal.Body>

          <Button className="custom-modal-button" onClick={handleSuccessClose}>
            Aceptar
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AddProveedorForm;